"use client";

import { useRef, useState } from "react";
import { Play } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Locale } from "@/lib/i18n";

/**
 * "What is Progsity", the 60 second explainer, as a click-to-play card.
 *
 * - One MP4 per locale under /public/video with the voice-over and captions in that
 *   locale. Nothing is fetched until the visitor presses play: the card is a poster
 *   image and a button, so the About page and the homepage pay nothing for it.
 * - Plays with sound (a click is a user gesture) and hands over to the native
 *   controls, which is the right thing for a film people choose to watch.
 */
const SOURCES: Record<Locale, { src: string; poster: string }> = {
  en: { src: "/video/what-is-progsity.en.mp4", poster: "/video/what-is-progsity.en.jpg" },
  bn: { src: "/video/what-is-progsity.bn.mp4", poster: "/video/what-is-progsity.bn.jpg" },
};

const LABELS: Record<Locale, { play: string; aria: string; length: string }> = {
  en: { play: "Watch: what is Progsity", aria: "What is Progsity, a one minute explainer", length: "1 min" },
  bn: { play: "দেখুন: Progsity আসলে কী", aria: "Progsity আসলে কী, এক মিনিটে", length: "১ মিনিট" },
};

export function ExplainerVideo({ locale, className }: { locale: Locale; className?: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [started, setStarted] = useState(false);
  const source = SOURCES[locale] ?? SOURCES.en;
  const labels = LABELS[locale] ?? LABELS.en;

  const start = () => {
    const v = videoRef.current;
    if (!v) return;
    setStarted(true);
    v.src = source.src;
    v.play().catch(() => undefined);
  };

  return (
    <div
      className={cn(
        "relative mx-auto w-full max-w-4xl overflow-hidden rounded-[calc(var(--radius)+6px)] border border-[rgb(var(--border))] bg-black shadow-card",
        className,
      )}
    >
      <video
        ref={videoRef}
        className="aspect-video w-full"
        poster={source.poster}
        preload="none"
        playsInline
        controls={started}
        aria-label={labels.aria}
      />
      {!started ? (
        // `SITE-HOME02` (review item H5): the pill sits in the poster's
        // top-left corner, the one part the poster leaves empty; centred it
        // covered the painted Practice card, and the bottom edge carries the
        // poster's caption. On a phone it is the play button and the length.
        <button
          type="button"
          onClick={start}
          aria-label={labels.play}
          className="group absolute inset-0 flex items-start justify-start p-2.5 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/40 sm:p-5"
        >
          <span className="flex items-center gap-2 rounded-full bg-white/95 py-1.5 pl-1.5 pr-3.5 text-sm font-semibold text-[rgb(var(--foreground))] shadow-lg transition-transform group-hover:scale-[1.03] sm:gap-3 sm:py-3 sm:pl-4 sm:pr-6">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-white sm:h-10 sm:w-10">
              <Play className="ml-0.5 h-5 w-5" />
            </span>
            <span className="hidden sm:inline">{labels.play}</span>
            <span className="text-[rgb(var(--muted-foreground))]">
              <span aria-hidden className="hidden sm:inline">· </span>
              {labels.length}
            </span>
          </span>
        </button>
      ) : null}
    </div>
  );
}
